// The Requested Byes grid: which rounds each player has asked to sit out.
//
// One row per player, one cell per round, each cell cycling playing -> ½ -> 0 -> playing.
// A grid rather than a field on the player form because the question an arbiter actually
// has is "who is missing on Saturday afternoon", which is a column, and because three
// people asking for round 3 is three clicks here against three trips through a modal.
//
// The rules and the storage shape live in js/core/bye-requests.js; this file reads inputs,
// writes inputs, and shows or hides things.

import { state, saveToLocalStorage, getActiveSettings } from '../state.js';
import { formatPlayerName, escapeHtml } from '../utils/formatting.js';

const modal = document.getElementById('requested-byes-modal');
const headerRow = document.getElementById('requested-byes-header-row');
const listBody = document.getElementById('requested-byes-list');
const searchInput = document.getElementById('requested-byes-search');
const openButton = document.getElementById('btn-requested-byes');

// Refreshes the roster behind the modal, so the badge on a player's row keeps up with the
// grid. Injected by app.js rather than imported: js/ui/players.js already imports THIS
// module for the button, and importing it back would make the two circular for the sake of
// one call.
let onChange = () => {};

// What each state looks like in a cell. The symbols are the ones a pairing list already
// uses for the same things, so an arbiter reads them without a legend.
const CELL = {
    none: { text: '–', color: 'var(--text-muted)', weight: '400', label: 'Plays this round' },
    half: { text: '½', color: 'var(--warning)', weight: '700', label: 'Sits out — half a point' },
    zero: { text: '0', color: 'var(--danger)', weight: '700', label: 'Sits out — no points' }
};

/**
 * How many rounds the grid offers. The tournament's configured round count, but never
 * fewer than the rounds that actually exist: a tournament whose round count was lowered
 * after it started would otherwise hide the columns for rounds already played, and the
 * arbiter would be looking at a grid that disagrees with the pairing list.
 */
function roundCount() {
    const settings = getActiveSettings();
    return Math.max(parseInt(settings.roundsCount, 10) || 0, state.rounds.length);
}

/** Whether this tournament can honour a requested bye at all. */
function formatHonoursByes() {
    return window.ByeRequests.honoursFormat(getActiveSettings().format);
}

/**
 * Show the button only where it means something: an arbiter, on a tournament whose format
 * honours a request, with rounds left to request. Called from renderPlayerList() because
 * that is what runs whenever the roster or the open tournament changes.
 */
export function syncRequestedByesButton() {
    if (!openButton) return;
    const usable = !state.isSpectatorMode
        && !!state.activeTournament
        && formatHonoursByes()
        && roundCount() > state.rounds.length;
    openButton.style.display = usable ? '' : 'none';
}

function playersForGrid() {
    const query = (searchInput ? searchInput.value : '').toLowerCase().trim();
    return state.players
        .filter(p => !query || formatPlayerName(p, false).toLowerCase().includes(query))
        .slice()
        .sort((a, b) => (parseInt(a.startNo, 10) || 9999) - (parseInt(b.startNo, 10) || 9999)
            || formatPlayerName(a, false).localeCompare(formatPlayerName(b, false)));
}

function cellHtml(player, round, paired) {
    // A round that has been paired is history. Its bye — if there was one — is on the
    // pairing list as a result, and changing the request now would change nothing except
    // what this grid claims happened.
    if (paired) {
        return `<td style="text-align: center; color: var(--text-muted); opacity: 0.45;"
                    title="${escapeHtml(tr('Round {n} has already been paired', { n: round }))}">·</td>`;
    }
    const value = window.ByeRequests.requestFor(player, round) || 'none';
    const look = CELL[value];
    return `<td style="text-align: center; padding: 0.15rem;">
        <button type="button" class="bye-cell" data-player="${escapeHtml(player.id)}" data-round="${round}"
            title="${escapeHtml(tr(look.label))}"
            style="width: 32px; height: 28px; border: 1px solid var(--border-color); border-radius: 6px;
                   background: transparent; cursor: pointer; font-size: 0.95rem; line-height: 1;
                   color: ${look.color}; font-weight: ${look.weight};">${look.text}</button>
    </td>`;
}

export function renderRequestedByes() {
    if (!listBody || !headerRow) return;

    const rounds = roundCount();
    const played = state.rounds.length;

    headerRow.innerHTML = `<th style="min-width: 180px;">${tr('Player')}</th>`
        + Array.from({ length: rounds }, (_, i) => {
            const n = i + 1;
            const done = n <= played;
            return `<th style="width: 40px; text-align: center; ${done ? 'opacity: 0.45;' : ''}">${n}</th>`;
        }).join('');

    const players = playersForGrid();
    if (players.length === 0) {
        listBody.innerHTML = `<tr><td colspan="${rounds + 1}" style="text-align: center; padding: 2rem; color: var(--text-muted);">${tr('No players match the current filters.')}</td></tr>`;
        return;
    }

    listBody.innerHTML = players.map(p => {
        const cells = Array.from({ length: rounds }, (_, i) => cellHtml(p, i + 1, i + 1 <= played)).join('');
        return `<tr>
            <td><div class="tbl-title">${formatPlayerName(p, true)}</div></td>
            ${cells}
        </tr>`;
    }).join('');

    listBody.querySelectorAll('.bye-cell').forEach(btn => {
        btn.addEventListener('click', () => cycleCell(btn.dataset.player, parseInt(btn.dataset.round, 10)));
    });
}

function cycleCell(playerId, round) {
    if (state.isSpectatorMode) return;
    const player = state.players.find(p => String(p.id) === String(playerId));
    if (!player) return;

    const next = window.ByeRequests.nextValue(window.ByeRequests.requestFor(player, round));
    const updated = window.ByeRequests.withRequest(player.byeRequests, round, next);

    // An empty map is deleted rather than stored. Every player object carries this field
    // otherwise, for a feature most tournaments never use, into every sync and every
    // export — and `undefined` is the one value Firestore refuses outright.
    if (Object.keys(updated).length === 0) delete player.byeRequests;
    else player.byeRequests = updated;

    saveToLocalStorage();
    renderRequestedByes();
    // The roster carries a badge for every upcoming bye, and it is visible behind this
    // modal — leaving it stale would have it disagree with the grid on screen.
    onChange();
}

export function setupRequestedByes(deps = {}) {
    if (typeof deps.onChange === 'function') onChange = deps.onChange;
    if (openButton) {
        openButton.addEventListener('click', () => {
            if (searchInput) searchInput.value = '';
            renderRequestedByes();
            if (modal) modal.classList.add('active');
        });
    }
    if (searchInput) searchInput.addEventListener('input', renderRequestedByes);
    syncRequestedByesButton();
}
